import {Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {finalize, map, of, Subject, switchMap} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {SiteModel} from "../../../../../models/site.model";
import {UnityModel} from "../../../../../models/unity.model";
import {UserModel, SiteAssignment} from "../../../../../models/user.model";
import {UsersService} from "../../../../shared/services/users.service";
import {RefApiService} from "../../../../shared/services/ref-api.service";
import {SitesService} from "../../../../shared/services/sites.service";
import {UnitiesService} from "../../../../shared/services/unities.service";
import {AgilUserModel} from "../../../../../models/agil-user.model";
import {RoleOption, UserRole} from "../../../../../enums/user-role.enum";
import {TranslateService} from "@ngx-translate/core";
import {NotificationService} from "../../../../shared/services/notification.service";
import {getRoleOptions} from "../../../../../shared/utils/visible-roles.utils";
import {UserService} from "../../../../user/services/user.service";
import {MatSelectChange, MatSelect} from "@angular/material/select";
import {MatOptionSelectionChange} from "@angular/material/core";

interface DialogData {
    user?: UserModel;
}

@Component({
    selector: 'app-user-modal',
    templateUrl: './user-modal.component.html',
    styleUrls: ['./user-modal.component.scss']
})
export class UserModalComponent implements OnInit, OnDestroy {
    @ViewChild('searchContainer', { static: false }) searchContainer!: ElementRef;

    private destroy$ = new Subject<void>();
    userForm!: FormGroup;
    filteredUsers: AgilUserModel[] = [];
    sites: SiteModel[] = [];
    unities: UnityModel[] = [];
    unitiesBySite: { [siteId: string]: UnityModel[] } = {};
    siteAssignments: { [siteId: string]: { hasAllUnities: boolean; unityIds: string[] } } = {};
    showSearchResults: boolean = false;
    isSearching: boolean = false;
    isSubmitting = false;
    isEditMode: boolean = false;
    isInitializing: boolean = false;
    roles: RoleOption[] = [];
    protected readonly UserRole = UserRole;
    currentUser: UserModel | null = null;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<UserModalComponent>,
        @Inject(MAT_DIALOG_DATA) private data: DialogData,
        private usersService: UsersService,
        private refApiService: RefApiService,
        private sitesService: SitesService,
        private unitiesService: UnitiesService,
        private translate: TranslateService,
        private notificationService: NotificationService,
        private userService: UserService,
    ) {
        this.isEditMode = !!this.data?.user;
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        // Vérifier si le clic est à l'intérieur ou à l'extérieur du conteneur de recherche
        if (this.searchContainer && !this.searchContainer.nativeElement.contains(event.target)) {
            this.showSearchResults = false;
        }
    }

    toggleSearchResults(): void {
        const searchValue = this.userForm.get('searchUser')?.value;

        // Si la liste est déjà visible, on ne fait rien
        if (this.showSearchResults) {
            return;
        }

        // Si la recherche contient au moins 2 caractères, on affiche les résultats
        if (searchValue && searchValue.length >= 2) {
            this.showSearchResults = true;
            this.isSearching = true;
            // Forcer une nouvelle recherche pour afficher les résultats les plus récents
            this.refApiService.searchAgilUser(searchValue)
                .pipe(
                    finalize(() => this.isSearching = false)
                )
                .subscribe(users => {
                    this.filteredUsers = users.data;
                });
        }
    }

    ngOnInit(): void {
        this.initForm();
        this.currentUser = this.userService.getCurrentUser();
        this.roles = getRoleOptions(this.userService.getUser().value?.role);
        this.loadSites();
        
        if (!this.isEditMode) {
            this.setupSearchSubscription();
        }
        
        this.setupSiteChangeSubscription();
        this.setupRoleChangeSubscription();

        if (this.isEditMode) {
            this.populateForm();
        }
    }

    private initForm(): void {
        this.userForm = this.fb.group({
            searchUser: [''],
            first_name: [{value: '', disabled: false}, Validators.required],
            last_name: [{value: '', disabled: false}, Validators.required],
            igg: [{value: '', disabled: false}, Validators.required],
            email: [{value: '', disabled: false}, Validators.required],
            sites: [{value: [], disabled: false}],
            unities: [{value: [], disabled: false}],
            role: ['', Validators.required]
        });
    }

    private setupRoleChangeSubscription(): void {
        this.userForm.get('role')?.valueChanges.pipe(
            map(role => {
                return role === UserRole.SUPER_ADMIN
            }),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(isSuperAdmin => {
            const sitesControl = this.userForm.get('sites');
            const unitiesControl = this.userForm.get('unities');

            if (isSuperAdmin) {
                sitesControl?.disable();
                unitiesControl?.disable();
                sitesControl?.setValue([]);
                unitiesControl?.setValue([]);
                this.siteAssignments = {};
            } else {
                sitesControl?.enable();
                unitiesControl?.enable();
                sitesControl?.setValidators([Validators.required]);
            }

            sitesControl?.updateValueAndValidity();
            unitiesControl?.updateValueAndValidity();
        });
    }

    private populateForm(): void {
        this.isInitializing = true;
        const user = this.data.user!;
        const userSites = user.userSites || [];
        const selectedSiteIds = userSites.map(userSite => userSite.siteId);
        
        // Set basic user info first
        this.userForm.patchValue({
            first_name: user.firstName,
            last_name: user.lastName,
            igg: user.igg,
            email: user.email,
            role: user.role,
            sites: [], // Start with empty sites
            unities: []
        });

        if (selectedSiteIds.length > 0) {
            // Initialize site assignments first
            const unitiesFormValues: string[] = [];
            userSites.forEach(userSite => {
                this.siteAssignments[userSite.siteId] = {
                    hasAllUnities: userSite.hasAllUnities,
                    unityIds: userSite.hasAllUnities ? [] : (user.unities?.filter(u => u.siteId === userSite.siteId).map(unity => unity.id) || [])
                };
                
                // Set form values for unities
                if (userSite.hasAllUnities) {
                    unitiesFormValues.push('all-' + userSite.siteId);
                } else {
                    const siteUnityIds = user.unities?.filter(u => u.siteId === userSite.siteId).map(unity => unity.id) || [];
                    unitiesFormValues.push(...siteUnityIds);
                }
            });
            
            // Step 1: Simulate the working user interaction
            // First, trigger the sites selection (this will load unities)
            setTimeout(() => {
                this.userForm.patchValue({ sites: selectedSiteIds });
                
                // Step 2: Wait for unities to load, then set unity values
                this.unitiesService.getUnitiesBySites(selectedSiteIds)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (response) => {
                            this.unities = response.data;
                            // Group units by site
                            this.unitiesBySite = {};
                            this.unities.forEach(unity => {
                                if (!this.unitiesBySite[unity.siteId]) {
                                    this.unitiesBySite[unity.siteId] = [];
                                }
                                this.unitiesBySite[unity.siteId].push(unity);
                            });
                            
                            // Step 3: Wait for DOM to update, then set unities
                            setTimeout(() => {
                                this.isInitializing = false;
                                this.userForm.patchValue({ unities: unitiesFormValues });
                            }, 200);
                        },
                        error: (error) => {
                            console.error('Error loading unities:', error);
                            this.isInitializing = false;
                        }
                    });
            }, 50);
        } else {
            this.isInitializing = false;
        }
    }


    private setupSiteChangeSubscription(): void {
        this.userForm.get('sites')?.valueChanges
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(siteIds => {
                // Skip processing during initialization
                if (this.isInitializing) {
                    return;
                }
                
                if (siteIds && siteIds.length > 0) {
                    this.loadUnitiesForSites(siteIds);
                    // Initialize site assignments for new sites
                    siteIds.forEach((siteId: string) => {
                        if (!this.siteAssignments[siteId]) {
                            this.siteAssignments[siteId] = {
                                hasAllUnities: false,
                                unityIds: []
                            };
                        }
                    });
                    // Remove assignments for unselected sites
                    Object.keys(this.siteAssignments).forEach(siteId => {
                        if (!siteIds.includes(siteId)) {
                            delete this.siteAssignments[siteId];
                        }
                    });
                } else {
                    this.unitiesBySite = {};
                    this.siteAssignments = {};
                    this.userForm.get('unities')?.setValue([]);
                }
            });
    }

    private setupSearchSubscription(): void {
        this.userForm.get('searchUser')?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(value => {
                if (value?.length >= 2) {
                    this.showSearchResults = true;
                    this.isSearching = true;
                    return this.refApiService.searchAgilUser(value)
                        .pipe(
                            finalize(() => this.isSearching = false)
                        );
                } else {
                    this.filteredUsers = [];
                    this.showSearchResults = false;
                    return of(null);
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (response => {
                if (response) {
                    this.filteredUsers = response.data;
                }
            })
        });
    }

    private loadSites(): void {
        this.sitesService.getSites().subscribe(sites => {
            this.sites = sites.data;
        });
    }

    private loadUnitiesForSites(siteIds: string[]): void {
        this.unitiesService.getUnitiesBySites(siteIds)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.unities = response.data;
                    // Group units by site
                    this.unitiesBySite = {};
                    this.unities.forEach(unity => {
                        if (!this.unitiesBySite[unity.siteId]) {
                            this.unitiesBySite[unity.siteId] = [];
                        }
                        this.unitiesBySite[unity.siteId].push(unity);
                    });
                },
                error: (error) => {
                    console.error('Error loading unities:', error);
                }
            });
    }

    selectUser(user: AgilUserModel): void {
        this.userForm.patchValue({
            first_name: user.first_name.toLowerCase(),
            last_name: user.last_name.toLowerCase(),
            igg: user.igg.toLowerCase(),
            email: user.email.toLowerCase()
        });
        this.filteredUsers = [];
        this.showSearchResults = false;
    }


    onSubmit(): void {
        if (this.isFormDataComplete && !this.isSubmitting) {
            this.isSubmitting = true;
            const formValue = this.userForm.getRawValue();
            const userDto: any = {
                firstName: formValue.first_name,
                lastName: formValue.last_name,
                igg: formValue.igg,
                email: formValue.email,
                role: formValue.role,
            };

            if (formValue.role !== UserRole.SUPER_ADMIN) {
                userDto.siteAssignments = Object.keys(this.siteAssignments).map(siteId => {
                    const assignment = this.siteAssignments[siteId];

                    // If "All unities" is selected but current user doesn't have hasAllUnities,
                    // send the explicit list of unities instead
                    if (assignment.hasAllUnities && !this.currentUserHasAllUnitiesForSite(siteId)) {
                        return {
                            siteId: siteId,
                            hasAllUnities: false,
                            unityIds: this.unitiesBySite[siteId]?.map(u => u.id) || []
                        };
                    }

                    const result: any = {
                        siteId: siteId,
                        hasAllUnities: assignment.hasAllUnities
                    };

                    if (!assignment.hasAllUnities) {
                        result.unityIds = assignment.unityIds;
                    }

                    return result;
                });
            }

            const request = this.isEditMode
                ? this.usersService.update(this.data.user!.id!, userDto)
                : this.usersService.create(userDto);

            request.pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSubmitting = false)
            ).subscribe({
                next: (response) => {
                    const messageKey = this.isEditMode
                        ? 'pages.users_management.user_updated_successfully'
                        : 'pages.users_management.user_created_successfully';
                    this.notificationService.success(
                        this.translate.instant(messageKey),
                        this.translate.instant('general.actions.close'),
                        {duration: 3000}
                    );
                    this.dialogRef.close(response);
                },
                error: () => {
                    const messageKey = this.isEditMode
                        ? 'pages.users_management.user_update_failed'
                        : 'pages.users_management.user_creation_failed';
                    this.notificationService.error(
                        this.translate.instant(messageKey),
                        this.translate.instant('general.actions.close'),
                        {duration: 3000}
                    );
                }
            });
        }
    }


    closeModal(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Gère la sélection/désélection de "Toutes les unités" pour un site donné
     */
    toggleAllUnits(siteId: string, event: MatOptionSelectionChange): void {
        if (!event.isUserInput) return;

        const control = this.userForm.get('unities')!;
        const currentValues: string[] = control.value || [];
        const siteUnityIds = this.unitiesBySite[siteId]?.map(u => u.id) || [];
        const allValue = `all-${siteId}`;

        let updatedValues: string[];

        if (event.source.selected) {
            // Activation de "All" : supprimer les unités individuelles de ce site
            updatedValues = [
                ...currentValues.filter(v => !siteUnityIds.includes(v)),
                allValue
            ];
            this.siteAssignments[siteId] = { hasAllUnities: true, unityIds: [] };
        } else {
            // Désactivation de "All" : supprimer uniquement l'option "All"
            updatedValues = currentValues.filter(v => v !== allValue);
            this.siteAssignments[siteId] = { hasAllUnities: false, unityIds: [] };
        }

        control.setValue(updatedValues, { emitEvent: false });
    }

    /**
     * Gère la sélection des unités individuelles et résout les conflits avec les options "All"
     */
    onUnitySelectionChange(event: MatSelectChange): void {
        const selectedValues = event.value as string[];
        const siteIds = this.userForm.get('sites')!.value as string[];
        
        let resolvedValues = [...selectedValues];
        
        siteIds.forEach(siteId => {
            this.updateSiteAssignment(siteId, selectedValues, resolvedValues);
        });

        // Conserver uniquement les unités individuelles (pas les options "all-")
        const individualUnitsOnly = resolvedValues.filter(v => !v.startsWith('all-'));
        this.userForm.get('unities')!.setValue(individualUnitsOnly, { emitEvent: false });
    }

    /**
     * Met à jour l'assignment d'un site en résolvant les conflits entre "All" et unités individuelles
     */
    private updateSiteAssignment(siteId: string, selectedValues: string[], resolvedValues: string[]): void {
        const allValue = `all-${siteId}`;
        const hasAllSelected = selectedValues.includes(allValue);
        const siteUnityIds = this.unitiesBySite[siteId]?.map(u => u.id) || [];
        const individualUnitsForSite = selectedValues.filter(v => siteUnityIds.includes(v));
        
        // Conflit : "All" et unités individuelles sélectionnées pour le même site
        if (hasAllSelected && individualUnitsForSite.length > 0) {
            // Privilégier les unités individuelles, supprimer "All"
            const allIndex = resolvedValues.indexOf(allValue);
            if (allIndex !== -1) {
                resolvedValues.splice(allIndex, 1);
            }
            this.siteAssignments[siteId] = {
                hasAllUnities: false,
                unityIds: individualUnitsForSite
            };
        }
        // Seules des unités individuelles sont sélectionnées
        else if (!hasAllSelected && individualUnitsForSite.length > 0) {
            this.siteAssignments[siteId] = {
                hasAllUnities: false,
                unityIds: individualUnitsForSite
            };
        }
        // Aucune unité sélectionnée pour ce site
        else if (!hasAllSelected && individualUnitsForSite.length === 0) {
            this.siteAssignments[siteId] = {
                hasAllUnities: false,
                unityIds: []
            };
        }
        // Si seul "All" est sélectionné, ne rien faire (géré par toggleAllUnits)
    }

    getSiteLabel(siteId: string): string {
        const site = this.sites.find(s => s.id === siteId);
        return site ? site.label : siteId;
    }

    currentUserHasAllUnitiesForSite(siteId: string): boolean {
        if (!this.currentUser || !this.currentUser.userSites) {
            return false;
        }
        const userSite = this.currentUser.userSites.find(us => us.siteId === siteId);
        return userSite?.hasAllUnities ?? false;
    }

    isUnitySelectionValid(): boolean {
        const formValue = this.userForm.getRawValue();
        if (formValue.role === UserRole.SUPER_ADMIN) {
            return true;
        }

        if (!formValue.sites || formValue.sites.length === 0) {
            return false;
        }

        // Check if each selected site has valid assignment
        for (const siteId of formValue.sites) {
            const assignment = this.siteAssignments[siteId];
            if (!assignment || (!assignment.hasAllUnities && assignment.unityIds.length === 0)) {
                return false;
            }
        }

        return true;
    }

    get isDataReady(): boolean {
        if (!this.isEditMode) return true;
        return !this.isInitializing && Object.keys(this.unitiesBySite).length > 0;
    }

    trackBySiteId(_: number, siteId: string): string {
        return siteId;
    }

    trackByUnityId(_: number, unity: UnityModel): string {
        return unity.id;
    }

    get isFormDataComplete(): boolean {
        const formValue = this.userForm.getRawValue();
        const basicFieldsComplete = !!formValue.first_name &&
            !!formValue.last_name &&
            !!formValue.igg &&
            !!formValue.email &&
            !!formValue.role;

        if (formValue.role === UserRole.SUPER_ADMIN) {
            return basicFieldsComplete;
        }

        // Check if at least one site is selected
        if (!formValue.sites || formValue.sites.length === 0) {
            return false;
        }

        // Use the new validation method
        return basicFieldsComplete && this.isUnitySelectionValid();
    }


}
